import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';

const benefits = [
  { icon: 'ri-search-line', title: 'benefit_1_title', text: 'benefit_1_text' },
  { icon: 'ri-folder-line', title: 'benefit_2_title', text: 'benefit_2_text' },
  { icon: 'ri-bar-chart-line', title: 'benefit_3_title', text: 'benefit_3_text' },
  { icon: 'ri-shield-check-line', title: 'benefit_4_title', text: 'benefit_4_text' },
  { icon: 'ri-truck-line', title: 'benefit_5_title', text: 'benefit_5_text' },
];

const steps = [
  { num: '01', title: 'step_1', text: 'step_1_text' },
  { num: '02', title: 'step_2', text: 'step_2_text' },
  { num: '03', title: 'step_3', text: 'step_3_text' },
  { num: '04', title: 'step_4', text: 'step_4_text' },
  { num: '05', title: 'step_5', text: 'step_5_text' },
  { num: '06', title: 'step_6', text: 'step_6_text' },
  { num: '07', title: 'step_7', text: 'step_7_text' },
];

export default function Home() {
  const { t, i18n } = useTranslation();
  const [currentLang, setCurrentLang] = useState(i18n.language || 'pt');
  const lang = (currentLang as 'pt' | 'de' | 'en') || 'en';

  const handleLanguageChange = (lang: string) => {
    i18n.changeLanguage(lang);
    setCurrentLang(lang);
  };

  return (
    <div className="min-h-screen bg-background-50 font-body">
      <Navbar onLanguageChange={handleLanguageChange} currentLang={currentLang} />

      {/* Hero Section */}
      <section className="relative min-h-[600px] md:min-h-[700px] flex items-center justify-center overflow-hidden pt-16">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Artistic%20abstract%20fashion%20collage%20background%20with%20flowing%20fabric%20and%20warm%20terracotta%20and%20cream%20tones%2C%20soft%20light%20gradients%2C%20premium%20editorial%20aesthetic%2C%20elegant%20minimal%20composition%2C%20high%20end%20lifestyle%20mood&width=1600&height=900&seq=hero-main-02&orientation=landscape&nocache=true"
            alt="CSSBuy Spreadsheet"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/50 to-black/60"></div>
        </div>

        <div className="relative z-10 w-full px-4 md:px-6 lg:px-8 text-center">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
              {t('hero_title')}
            </h1>
            <p className="text-base md:text-lg lg:text-xl text-white/90 leading-relaxed mb-8 max-w-2xl mx-auto">
              {t('hero_subtitle')}
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="w-full sm:w-auto px-8 py-4 bg-primary-500 text-white font-semibold rounded-xl hover:bg-primary-600 transition-all whitespace-nowrap"
              >
                {t('hero_cta_primary')}
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="w-full sm:w-auto px-8 py-4 bg-white text-foreground-950 font-semibold rounded-xl hover:bg-background-100 transition-all whitespace-nowrap"
              >
                {t('hero_cta_secondary')}
              </a>
            </div>

            <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
              <span className="flex items-center gap-2 text-sm text-white/80">
                <i className="ri-check-line text-primary-400"></i>
                {t('hero_trust_1')}
              </span>
              <span className="flex items-center gap-2 text-sm text-white/80">
                <i className="ri-check-line text-primary-400"></i>
                {t('hero_trust_2')}
              </span>
              <span className="flex items-center gap-2 text-sm text-white/80">
                <i className="ri-check-line text-primary-400"></i>
                {t('hero_trust_3')}
              </span>
              <span className="flex items-center gap-2 text-sm text-white/80">
                <i className="ri-check-line text-primary-400"></i>
                {t('hero_trust_4')}
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* What Is Section */}
      <section className="w-full px-4 md:px-6 lg:px-8 py-16 md:py-24 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-6">
                {t('what_is_title')}
              </h2>
              <p className="text-base md:text-lg text-foreground-700 leading-relaxed mb-8">
                {t('what_is_text')}
              </p>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-white font-medium rounded-lg hover:bg-primary-600 transition-all whitespace-nowrap"
              >
                {t('what_is_cta')}
                <i className="ri-arrow-right-line"></i>
              </a>
            </div>
            <div className="relative">
              <img
                src="https://readdy.ai/api/search-image?query=Modern%20laptop%20showing%20an%20organized%20product%20spreadsheet%20on%20a%20bright%20desk%20with%20a%20small%20plant%20and%20coffee%2C%20warm%20natural%20light%2C%20productivity%20and%20fashion%20research%20scene%2C%20clean%20minimal%20workspace%2C%20editorial%20photography&width=800&height=600&seq=what-is-02&orientation=landscape&nocache=true"
                alt="CSSBuy Spreadsheet"
                className="w-full rounded-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="w-full px-4 md:px-6 lg:px-8 py-16 md:py-24 bg-background-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-4">
              {t('categories_title')}
            </h2>
            <p className="text-base md:text-lg text-foreground-600 max-w-2xl mx-auto">
              {t('categories_subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-6">
            {categories.map((cat) => (
              <a
                key={cat.key}
                href={cat.url}
                target="_blank"
                rel="noopener nofollow"
                className="group relative bg-white rounded-xl border border-background-200/70 overflow-hidden hover:border-primary-300/50 transition-all"
              >
                <div className="aspect-square overflow-hidden">
                  <img
                    src={cat.img}
                    alt={t(cat.key)}
                    title={t(cat.key)}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-3 md:p-4">
                  <p className="text-sm font-medium text-foreground-900 text-center group-hover:text-primary-600 transition-colors whitespace-nowrap">
                    {t(cat.key)}
                  </p>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="w-full px-4 md:px-6 lg:px-8 py-16 md:py-24 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-4">
              {t('benefits_title')}
            </h2>
            <p className="text-base md:text-lg text-foreground-600 max-w-2xl mx-auto">
              {t('benefits_subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className="bg-background-50 rounded-2xl p-6 md:p-8 border border-background-200/70 hover:border-primary-300/50 transition-all"
              >
                <div className="w-12 h-12 flex items-center justify-center bg-primary-100 rounded-xl mb-4">
                  <i className={`${benefit.icon} text-2xl text-primary-600`}></i>
                </div>
                <h3 className="text-lg font-semibold text-foreground-900 mb-2">
                  {t(benefit.title)}
                </h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  {t(benefit.text)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="w-full px-4 md:px-6 lg:px-8 py-16 md:py-24 bg-secondary-100">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-4">
              {t('how_title')}
            </h2>
            <p className="text-base md:text-lg text-foreground-600 max-w-2xl mx-auto">
              {t('how_subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {steps.map((step, index) => (
              <div
                key={index}
                className="relative bg-white rounded-2xl p-6 md:p-8 border border-background-200/70"
              >
                <span className="text-4xl font-bold text-primary-200 mb-4 block">
                  {step.num}
                </span>
                <h3 className="text-lg font-semibold text-foreground-900 mb-2">
                  {t(step.title)}
                </h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  {t(step.text)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Safety Section */}
      <section className="w-full px-4 md:px-6 lg:px-8 py-16 md:py-24 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="bg-accent-50 rounded-2xl p-8 md:p-12 border border-accent-200/50">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-10 h-10 flex items-center justify-center bg-accent-100 rounded-xl shrink-0">
                <i className="ri-shield-check-line text-xl text-accent-600"></i>
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950">
                {t('safety_title')}
              </h2>
            </div>
            <p className="text-base md:text-lg text-foreground-700 leading-relaxed mb-6 pl-14">
              {t('safety_text')}
            </p>
            <div className="pl-14">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 px-6 py-3 bg-accent-500 text-white font-medium rounded-lg hover:bg-accent-600 transition-all whitespace-nowrap"
              >
                {t('safety_cta')}
                <i className="ri-arrow-right-line"></i>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Articles Section */}
      <section className="w-full px-4 md:px-6 lg:px-8 py-16 md:py-24 bg-background-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-4">
              {t('articles_title')}
            </h2>
            <p className="text-base md:text-lg text-foreground-600 max-w-2xl mx-auto">
              {t('articles_subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {articles.map((article) => (
              <Link
                key={article.slug}
                to={`/article/${article.slug}`}
                className="group bg-white rounded-2xl border border-background-200/70 overflow-hidden hover:border-primary-300/50 hover:shadow-md transition-all flex flex-col"
              >
                <div className="relative h-44 md:h-48 overflow-hidden">
                  <img
                    src={article.image}
                    alt={article.title[lang]}
                    title={article.title[lang]}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <span className="absolute top-3 left-3 px-2.5 py-1 bg-white/95 text-primary-700 text-xs font-medium rounded-full">
                    {article.tag}
                  </span>
                </div>
                <div className="p-5 md:p-6 flex flex-col flex-1">
                  <span className="text-xs text-foreground-500 mb-2">{article.date}</span>
                  <h3 className="text-base md:text-lg font-bold text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors leading-snug">
                    {article.title[lang]}
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4 line-clamp-3">
                    {article.description[lang]}
                  </p>
                  <div className="flex items-center gap-2 text-primary-600 font-medium text-sm mt-auto">
                    {t('read_more')}
                    <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="w-full px-4 md:px-6 lg:px-8 py-16 md:py-24 bg-foreground-950">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-4">
            {t('cta_title')}
          </h2>
          <p className="text-base md:text-lg text-white/70 leading-relaxed mb-8 max-w-2xl mx-auto">
            {t('cta_subtitle')}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="w-full sm:w-auto px-8 py-4 bg-primary-500 text-white font-semibold rounded-xl hover:bg-primary-600 transition-all whitespace-nowrap"
            >
              {t('cta_explore')}
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="w-full sm:w-auto px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-all whitespace-nowrap border border-white/20"
            >
              {t('cta_main_site')}
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}